/**
 * Custom Hooks for Usage Warning System
 * Integrate usage monitoring and warnings into React components
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearDismissedWarnings,
  detectWarningThreshold,
  evaluateUsageWarnings,
  isWarningDismissed,
  saveDismissedWarning,
  trackWarningDismissal,
  updateLastWarningTimestamp,
} from '../services/usageWarningService';
import { ActiveUsageWarning } from '../types/billing.types';

/**
 * Minimum credit fields needed by the warning system. Both the standalone
 * billing page and the portal account page satisfy this contract; the latter
 * exposes `creditsTotal` instead of a precomputed `percentageUsed`.
 */
export interface UsageWarningInput {
  creditsUsed: number;
  creditsRemaining: number;
  percentageUsed?: number;
  totalMonthlyCredit?: number;
  creditsTotal?: number;
  periodStartDate?: string;
  periodEndDate?: string;
}

/** Derive a safe percentage when the caller does not provide one (AL-357). */
function getPercentageUsed(usage: UsageWarningInput): number {
  if (Number.isFinite(usage.percentageUsed)) {
    return usage.percentageUsed as number;
  }

  const total = usage.creditsTotal ?? usage.totalMonthlyCredit ?? 0;
  return total > 0 ? (usage.creditsUsed / total) * 100 : 0;
}

// ============================================================================
// useUsageWarnings Hook
// ============================================================================

/**
 * Hook to monitor usage and manage warnings
 * Automatically detects when thresholds are crossed and generates warnings
 * 
 * @param usage Current usage data
 * @param subscriptionId Subscription ID for tracking
 * @returns Object with warnings and control functions
 * 
 * @example
 * const { warnings, dismissWarning, hasActiveWarnings } = useUsageWarnings(usage, subId);
 */
export function useUsageWarnings(usage: UsageWarningInput | null, subscriptionId: string) {
  const [warnings, setWarnings] = useState<ActiveUsageWarning[]>([]);
  const previousPercentageRef = useRef<number>(-1); // Use ref to track previous percentage
  const currentPercentage = usage ? getPercentageUsed(usage) : 0;
  const creditsRemaining = usage?.creditsRemaining ?? 0;

  // Evaluate warnings whenever the relevant usage values change. Scalar
  // dependencies avoid render loops when callers construct the input inline.
  useEffect(() => {
    if (!usage || !subscriptionId) return;

    const previousPercentage = previousPercentageRef.current;


    // If credits used decreased below 70%, clear all warnings
    if (currentPercentage < 70) {
      setWarnings([]);
      clearDismissedWarnings();
    } else {
      // Normal threshold detection - only show warnings if at or above 70%
      let newThreshold = null;

      // On first render (previousPercentage = -1), always evaluate
      if (previousPercentage === -1) {
        // Determine which threshold to trigger
        if (currentPercentage >= 100) {
          newThreshold = 100;
        } else if (currentPercentage >= 90) {
          newThreshold = 90;
        } else if (currentPercentage >= 70) {
          newThreshold = 70;
        }
      } else {
        // On subsequent renders, only trigger if threshold is crossed
        newThreshold = detectWarningThreshold(
          currentPercentage,
          previousPercentage
        );
      }

      // Generate warnings if threshold was detected
      if (newThreshold !== null) {
        const newWarnings = evaluateUsageWarnings(
          currentPercentage,
          creditsRemaining,
          subscriptionId
        );

        // Filter out previously dismissed warnings
        const activeWarnings = newWarnings.filter(
          w => !isWarningDismissed(w.warningId)
        );

        setWarnings(activeWarnings);
        updateLastWarningTimestamp();
      }
    }

    // Update the refs for next comparison
    previousPercentageRef.current = currentPercentage;
  }, [creditsRemaining, currentPercentage, subscriptionId, usage !== null]);

  // Dismiss a specific warning
  const dismissWarning = useCallback((warningId: string) => {
    trackWarningDismissal(warningId);
    saveDismissedWarning(warningId);
    setWarnings(prev => prev.filter(w => w.warningId !== warningId));
  }, []);

  // Dismiss all warnings
  const dismissAllWarnings = useCallback(() => {
    warnings.forEach(w => {
      trackWarningDismissal(w.warningId);
      saveDismissedWarning(w.warningId);
    });
    setWarnings([]);
  }, [warnings]);

  // Check if any critical warnings exist
  const hasCriticalWarning = warnings.some(
    w => w.level === 'critical'
  );

  return {
    warnings,
    dismissWarning,
    dismissAllWarnings,
    hasActiveWarnings: warnings.length > 0,
    hasCriticalWarning,
    warningCount: warnings.length,
  };
}

// ============================================================================
// useUsageProgress Hook
// ============================================================================

/**
 * Hook to calculate and monitor usage progress
 * Provides formatted percentage, bars, and threshold indicators
 * 
 * @param usage Usage data
 * @returns Progress information for display
 */
export function useUsageProgress(usage: UsageWarningInput | null) {
  if (!usage) {
    return {
      percentage: 0,
      creditsUsed: 0,
      creditsRemaining: 0,
      creditsBudget: 0,
      isExhausted: false,
      isCritical: false,
      isWarning: false,
    };
  }

  const percentageUsed = getPercentageUsed(usage);
  return {
    percentage: Math.min(percentageUsed, 100),
    creditsUsed: usage.creditsUsed,
    creditsRemaining: Math.max(usage.creditsRemaining, 0),
    creditsBudget: usage.creditsTotal ?? usage.totalMonthlyCredit ?? 0,
    isExhausted: percentageUsed >= 100,
    isCritical: percentageUsed >= 90 && percentageUsed < 100,
    isWarning: percentageUsed >= 70 && percentageUsed < 90,
  };
}

// ============================================================================
// useWarningBanner Hook
// ============================================================================

/**
 * Hook to manage warning banner visibility and animations
 * Handles banner show/hide with animation support
 * 
 * @param warnings Active warnings
 * @returns Banner state and controls
 */
export function useWarningBanner(warnings: ActiveUsageWarning[]) {
  const [isVisible, setIsVisible] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (warnings.length > 0) {
      setIsVisible(true);
      setShouldAnimate(true);
    }
  }, [warnings]);

  const hideBanner = useCallback(() => {
    setIsVisible(false);
  }, []);

  const showBanner = useCallback(() => {
    setIsVisible(true);
    setShouldAnimate(true);
  }, []);

  return {
    isVisible,
    shouldAnimate,
    hideBanner,
    showBanner,
    hasWarnings: warnings.length > 0,
    primaryWarning: warnings[0] || null,
  };
}

// ============================================================================
// useBillingPeriodMonitor Hook
// ============================================================================

/**
 * Hook to monitor current billing period and days remaining
 * Useful for contextual messaging about period end
 * 
 * @param usage Usage data with period dates
 * @returns Period information
 */
export function useBillingPeriodMonitor(usage: UsageWarningInput | null) {
  const [periodInfo, setPeriodInfo] = useState({
    daysRemaining: 0,
    percentPeriodElapsed: 0,
    isEndOfPeriod: false,
    periodEndDate: new Date(),
  });

  useEffect(() => {
    if (!usage?.periodStartDate || !usage.periodEndDate) {
      setPeriodInfo({
        daysRemaining: 0,
        percentPeriodElapsed: 0,
        isEndOfPeriod: false,
        periodEndDate: new Date(),
      });
      return;
    }

    const startDate = new Date(usage.periodStartDate);
    const endDate = new Date(usage.periodEndDate);
    const now = new Date();

    const totalDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysElapsed = Math.ceil(
      (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysRemaining = Math.max(totalDays - daysElapsed, 0);

    const percentPeriodElapsed =
      totalDays > 0 ? Math.round((daysElapsed / totalDays) * 100) : 0;
    const isEndOfPeriod = daysRemaining <= 7; // Last 7 days

    setPeriodInfo({
      daysRemaining,
      percentPeriodElapsed,
      isEndOfPeriod,
      periodEndDate: endDate,
    });
  }, [usage?.periodEndDate, usage?.periodStartDate]);

  return periodInfo;
}

// ============================================================================
// useUsageAlerts Hook
// ============================================================================

/**
 * Combined hook that integrates all usage monitoring
 * Provides comprehensive usage alert system
 * 
 * @param usage Usage data
 * @param subscriptionId Subscription ID
 * @returns Complete alert state
 */
export function useUsageAlerts(usage: UsageWarningInput | null, subscriptionId: string) {
  const { warnings, dismissWarning, hasCriticalWarning } = useUsageWarnings(
    usage,
    subscriptionId
  );
  const progressData = useUsageProgress(usage);
  const bannerState = useWarningBanner(warnings);
  const periodInfo = useBillingPeriodMonitor(usage);

  return {
    // Warnings
    warnings,
    dismissWarning,
    hasCriticalWarning,
    warningCount: warnings.length,

    // Progress
    ...progressData,

    // Banner
    ...bannerState,

    // Period
    ...periodInfo,

    // Convenience
    shouldShowCriticalUI: hasCriticalWarning || progressData.isExhausted,
    shouldBlockActions: hasCriticalWarning && progressData.isExhausted,
  };
}
